<template>
    <form v-on:submit.prevent="submitForm">
        <p class="subheadline">Personen-Daten</p>
        <Role v-model:selected-role="formData.role" label-child="Kind" label-adult="Erziehungskraft"/>
        <div v-if="isChild">
            <Salutation id="child-salutation" :salutation-options="childSalutationOptions" v-model:salutation="formData.child.salutation"/>
            <div>
                <label for="child-firstName" class="small">Vorname Kind*</label>
                <input id="child-firstName" class="input" type="text" v-model="formData.child.firstName" placeholder="Vorname Kind" required>
            </div>
            <div>
                <label for="child-lastName" class="small">Nachname Kind*</label>
                <input id="child-lastName" class="input" type="text" v-model="formData.child.lastName" placeholder="Nachname Kind" required>
            </div>
            <div>
                <label for="child-dateOfBirth" class="small">Geburtsdatum Kind</label>
                <input id="child-dateOfBirth" class="input" type="date" :max="today"
                       v-model="formData.child.dateOfBirth">
            </div>
            <Group v-if="showGroupSelect" v-model:selected-group="formData.child.group" :available-groups="groups" label="Gruppe" />
            <div>
                <label for="child-startDate" class="small">Teilnahme der Essenversorgung ab*</label>
                <input id="child-startDate" class="input" type="date" :min="today" required
                       v-model="formData.child.dateStart">
                <p>
                    Hinweis: Ab diesem Tag wird dann die Dauerbestellung in Ihrem Kundenkonto hinterlegt
                </p>
            </div>
        </div>
        <!-- Debug: role={{ formData.role }}, groups.length={{ groups.length }}, showAdultGroupSelect={{ showAdultGroupSelect }} -->
        <Group v-if="showAdultGroupSelect" v-model:selected-group="formData.group" :available-groups="groups" label="Gruppe" />
        <BillingAddress
            :headline="headlineBillingAddress"
            :include-optional-company="false"
            :include-optional-date-of-birth="true"
            :salutation-options="billingAddressSalutationOptions"
            v-model:salutation="formData.billingAddress.salutation"
            v-model:first-name="formData.billingAddress.firstName"
            v-model:last-name="formData.billingAddress.lastName"
            v-model:street="formData.billingAddress.street"
            v-model:zip-code="formData.billingAddress.zipCode"
            v-model:city="formData.billingAddress.city"
            v-model:phone="formData.billingAddress.phone"
            v-model:email="formData.billingAddress.email"
            v-model:date-of-birth="formData.billingAddress.dateOfBirth"
        />
        <Payment
            v-model:payment-method="formData.payment.method"
            v-model:bank-account-owner="formData.payment.bankAccountOwner"
            v-model:bank-name="formData.payment.bankName"
            v-model:bank-bic="formData.payment.bankBic"
            v-model:bank-iban="formData.payment.bankIban"
            v-model:bic-error="bicError"
            v-model:iban-error="ibanError"
            :allowed-payment-methods="allowedPaymentMethods"
        />
        <Password
            v-model:value="formData.password.value"
            v-model:compare="formData.password.compare"
            v-model:error="passwordError"
        />
        <OptionsAndTermsAndCondition
            v-model:billing-via-email="formData.billingViaEmail"
            v-model:subscribe-to-newsletter="formData.subscribeToNewsletter"
            v-model:accepted-terms-and-conditions="formData.acceptedTermsAndConditions"
        />
        <SubmitButton />
    </form>
</template>

<script>
import Role from '@/views/components/ui/Register/Step1/Form/Part/Role'
import Salutation from '@/views/components/ui/Register/Step1/Form/Part/Salutation'
import Group from '@/views/components/ui/Register/Step1/Form/Part/Group'
import BillingAddress from '@/views/components/ui/Register/Step1/Form/Part/BillingAddress'
import Payment from '@/views/components/ui/Register/Step1/Form/Part/Payment'
import Password from '@/views/components/ui/Register/Step1/Form/Part/Password'
import OptionsAndTermsAndCondition from '@/views/components/ui/Register/Step1/Form/Part/OptionsAndTermsAndCondition'
import SubmitButton from '@/views/components/ui/Register/Step1/Form/Part/SubmitButton'

import {RegistrationRole, allowedPaymentMethods, Destination} from '@/utils/registration'
import {format} from "date-fns";

export default {
    name: 'RegisterStep1FormKita',
    components: {
        Role,
        Salutation,
        Group,
        BillingAddress,
        Payment,
        Password,
        OptionsAndTermsAndCondition,
        SubmitButton,
    },
    props: {
        registrationFormOptions: {
            type: Object,
            required: true,
        },
        initialRegistrationData: [Object, null]
    },
    data() {
        return {
            formData: {
                role: this.initialRegistrationData?.role ?? RegistrationRole.Child,
                group: this.initialRegistrationData?.group ?? '',
                child: {
                    salutation: this.initialRegistrationData?.child?.salutation ?? '',
                    firstName: this.initialRegistrationData?.child?.firstName ?? '',
                    lastName: this.initialRegistrationData?.child?.lastName ?? '',
                    dateOfBirth: this.initialRegistrationData?.child?.dateOfBirth ?? '',
                    group: this.initialRegistrationData?.child?.group ?? '',
                    dateStart: this.initialRegistrationData?.child?.dateStart ?? '',
                },
                billingAddress: {
                    salutation: this.initialRegistrationData?.billingAddress?.salutation ?? '',
                    firstName: this.initialRegistrationData?.billingAddress?.firstName ?? '',
                    lastName: this.initialRegistrationData?.billingAddress?.lastName ?? '',
                    street: this.initialRegistrationData?.billingAddress?.street ?? '',
                    zipCode: this.initialRegistrationData?.billingAddress?.zipCode ?? '',
                    city: this.initialRegistrationData?.billingAddress?.city ?? '',
                    phone: this.initialRegistrationData?.billingAddress?.phone ?? '',
                    email: this.initialRegistrationData?.billingAddress?.email ?? '',
                    dateOfBirth: this.initialRegistrationData?.billingAddress?.dateOfBirth ?? '',
                },
                payment: {
                    method: this.initialRegistrationData?.payment?.method ?? '',
                    bankAccountOwner: this.initialRegistrationData?.payment?.bankAccountOwner ?? '',
                    bankName: this.initialRegistrationData?.payment?.bankName ?? '',
                    bankBic: this.initialRegistrationData?.payment?.bankBic ?? '',
                    bankIban: this.initialRegistrationData?.payment?.bankIban ?? ''
                },
                password: {
                    value: this.initialRegistrationData?.password?.value ?? '',
                    compare: this.initialRegistrationData?.password?.compare ?? '',
                },
                billingViaEmail: this.initialRegistrationData?.billingViaEmail ?? false,
                subscribeToNewsletter: this.initialRegistrationData?.subscribeToNewsletter ?? false,
                acceptedTermsAndConditions: this.initialRegistrationData?.acceptedTermsAndConditions ?? false,
            },
            passwordError: '',
            ibanError: '',
            bicError: '',
        }
    },
    computed: {
        today() {
            return format(new Date(), 'yyyy-MM-dd')
        },
        isChild() {
            return this.formData.role === RegistrationRole.Child;
        },
        showGroupSelect() {
            return this.formData.role === RegistrationRole.Child && this.groups.length > 0
        },
        showAdultGroupSelect() {
            return this.formData.role === RegistrationRole.Adult && this.groups.length > 0
        },
        headlineBillingAddress() {
            if (this.formData.role === RegistrationRole.Child) {
                return 'Rechnungsanschrift (erziehungsberechtigte Person)';
            }

            return 'Rechnungsanschrift (Erziehungskraft)';
        },
        allowedPaymentMethods() {
            return allowedPaymentMethods(Destination.Kita);
        },
        groups() {
            if (Array.isArray(this.registrationFormOptions.institutionGroups)) {
                return this.registrationFormOptions.institutionGroups
            }

            return []
        },
        billingAddressSalutationOptions() {
            if (Array.isArray(this.registrationFormOptions.salutationOptions.billingAddress)) {
                return this.registrationFormOptions.salutationOptions.billingAddress
            }
            return [];
        },
        childSalutationOptions() {
            if (Array.isArray(this.registrationFormOptions.salutationOptions.child)) {
                return this.registrationFormOptions.salutationOptions.child
            }
            return [];
        }
    },
    methods: {
        submitForm() {
            if (this.passwordError || this.ibanError || this.bicError) {
                return
            }

            this.$emit('submitted', this.formData);
        }
    },
    emits: [
        'submitted'
    ]
}
</script>
